import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getParticipantFromAppsScript } from '@/services/registration';
import { QRCodeCanvas } from 'qrcode.react';
import html2canvas from 'html2canvas';
import type { Participant } from '@/types';

export default function DownloadTicketPage() {
  const { participantId } = useParams();
  const ticketRef = useRef<HTMLDivElement>(null);

  const [participant, setParticipant] = useState<Participant | null>(null);
  const [status, setStatus] = useState<'loading' | 'downloading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const loadAndDownload = async () => {
      if (!participantId) {
        setStatus('error');
        setErrorMessage('No ticket ID provided');
        return;
      }

      try {
        // Fetch participant data
        const data = await getParticipantFromAppsScript(participantId);

        if (!data) {
          setStatus('error');
          setErrorMessage('Ticket not found. Please check your email for the correct link.');
          return;
        }

        setParticipant(data);
        setStatus('downloading');

        // Wait for the ticket to render
        await new Promise(resolve => setTimeout(resolve, 500));

        // Download the ticket
        await downloadTicket(data.fullName);
      } catch (error) {
        console.error('Error loading ticket:', error);
        setStatus('error');
        setErrorMessage('Failed to load ticket. Please try again.');
      }
    };

    loadAndDownload();
  }, [participantId]);

  const downloadTicket = async (name: string) => {
    if (!ticketRef.current) return;

    try {
      const canvas = await html2canvas(ticketRef.current, {
        scale: 2,
        backgroundColor: '#faf5e4',
        logging: false,
        useCORS: true,
      });

      const link = document.createElement('a');
      link.download = `Greater-Heights-Ticket-${name.replace(/\s+/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      setStatus('success');
    } catch (error) {
      console.error('Download error:', error);
      setStatus('error');
      setErrorMessage('Failed to download ticket. Please try again.');
    }
  };

  const handleRetryDownload = () => {
    if (participant) {
      setStatus('downloading');
      setTimeout(() => downloadTicket(participant.fullName), 100);
    }
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream via-white to-cream/50 flex items-center justify-center px-6">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-6">
            <svg className="animate-spin text-crimson w-full h-full" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
          <h2 className="text-2xl font-display font-bold text-earth-brown mb-2">Loading Your Ticket</h2>
          <p className="text-earth-brown/60">Please wait...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream via-white to-cream/50 flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-white/80 backdrop-blur-xl rounded-3xl shadow-large border border-white p-12 text-center">
          <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl font-display font-bold text-earth-brown mb-4">Download Failed</h2>
          <p className="text-earth-brown/70 mb-8">{errorMessage}</p>
          <a
            href="/"
            className="inline-block px-6 py-4 bg-crimson text-white font-semibold rounded-2xl hover:bg-crimson-dark transition-all"
          >
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-cream/50 py-12 px-6">
      <div className="max-w-2xl mx-auto">
        {/* Status Message */}
        <div className="text-center mb-8">
          {status === 'downloading' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4">
                <svg className="animate-spin text-crimson w-full h-full" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
              <h2 className="text-2xl font-display font-bold text-earth-brown mb-2">Downloading Your Ticket...</h2>
              <p className="text-earth-brown/60">Your download should start automatically.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-10 h-10 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-2xl font-display font-bold text-earth-brown mb-2">Download Complete!</h2>
              <p className="text-earth-brown/60 mb-6">Your ticket has been saved to your device.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleRetryDownload}
                  className="px-6 py-3 bg-crimson text-white font-semibold rounded-xl hover:bg-crimson-dark transition-all"
                >
                  Download Again
                </button>
                <a
                  href="/"
                  className="px-6 py-3 bg-white text-earth-brown font-semibold rounded-xl border-2 border-earth-tan/50 hover:border-earth-tan transition-all"
                >
                  Return to Home
                </a>
              </div>
            </>
          )}
        </div>

        {/* Ticket (for rendering/download) */}
        {participant && (
          <div ref={ticketRef} className="bg-cream p-6">
            {/* Ticket Container */}
            <div className="bg-white rounded-3xl shadow-large overflow-hidden border-4 border-crimson max-w-lg mx-auto">
              {/* Ticket Header */}
              <div className="relative bg-gradient-to-r from-crimson to-crimson-dark p-6 text-white text-center">
                <h1 className="text-3xl font-display font-bold mb-1">Greater Heights</h1>
                <p className="text-cream/90">Holy Week Retreat 2026</p>
                <div className="absolute -bottom-3 left-4 w-6 h-6 bg-cream rounded-full" />
                <div className="absolute -bottom-3 right-4 w-6 h-6 bg-cream rounded-full" />
              </div>

              {/* Dotted Separator */}
              <div className="border-t-2 border-dashed border-earth-tan/30" />

              {/* Ticket Body */}
              <div className="p-6 text-center">
                {/* QR Code */}
                <div className="inline-block bg-white p-4 rounded-2xl border-4 border-crimson shadow-medium mb-4">
                  <QRCodeCanvas
                    value={JSON.stringify({
                      id: participant.id,
                      event: 'greater-heights-2026',
                    })}
                    size={160}
                    level="H"
                    includeMargin={true}
                    bgColor="#ffffff"
                    fgColor="#b43c3a"
                  />
                </div>

                {/* Participant Info */}
                <h2 className="text-xl font-bold text-earth-brown mb-1">{participant.fullName}</h2>
                <p className="text-sm text-earth-brown/60 mb-4">
                  ID: {participant.id.slice(0, 8).toUpperCase()}
                </p>

                {/* Check-in PIN */}
                <div className="bg-crimson/10 rounded-xl p-4 mb-4 inline-block">
                  <p className="text-xs text-earth-brown/60 uppercase tracking-wide mb-1">Check-in PIN</p>
                  <p className="text-2xl font-bold font-mono text-crimson tracking-wider">
                    {participant.checkInPin}
                  </p>
                </div>

                {/* Event Details */}
                <div className="text-sm text-earth-brown/70 space-y-1">
                  <p><strong>Date:</strong> April 2-3, 2026</p>
                  <p><strong>Venue:</strong> Matrix Creation Events</p>
                  <p><strong>Address:</strong> Malakas St., Quezon City</p>
                </div>
              </div>

              {/* Ticket Footer */}
              <div className="bg-cream border-t-2 border-dashed border-earth-tan/30 px-6 py-4 text-center">
                <p className="text-xs text-earth-brown/50">
                  Present this QR code at registration for check-in
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
